import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorStatus,
  apiErrorText,
  useCreateFleetModelMutation,
  useLazyGetFleetModelQuery,
  useListFleetModelsQuery,
  useUpdateFleetModelMutation,
  type FleetCategory,
  type FleetModel,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { DrawerBody, DrawerFooter } from '@shared/ui/Drawer';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import {
  BlockHeading,
  BlockNote,
  BlockSection,
  FLEET_MODEL_STATUSES,
  FLEET_STATUS_LABEL,
  FORM_KEY,
  PassportBlockNav,
  blockAnchor,
  blockNumber,
  buildModelPayload,
  buildPassportForm,
  changedKeys,
  fleetType,
  formKeyBlock,
  formKeyLabel,
  initialFormValues,
  isFleetStatus,
  jumpToBlock,
  nameKey,
  rebaseForm,
  typo,
  unknownPassportEntries,
} from '@entities/fleet';
import {
  Control,
  ControlArea,
  FieldShell,
  FormGrid,
  MatrixInput,
  PairsInput,
  ScalarInput,
  type FieldBinding,
  type SuggestList,
} from './formFields';
import { Actions, FooterStatus, Notice, TextButton } from './ui';

const FUEL_KINDS = ['АИ', 'ДТ', 'ГАЗ'];
const SUGGEST_LISTS: readonly SuggestList[] = ['owner', 'position', 'fuel'];

const Legend = styled.p`
  margin: ${({ theme }) => `${theme.spacing(4)} 0 0`};
  font-size: ${fleetType.small};
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const Notices = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  &:empty {
    display: none;
  }
`;

const Extras = styled.dl`
  margin: 0;
  font-size: ${fleetType.body};

  & > div {
    display: flex;
    gap: ${({ theme }) => theme.spacing(3)};
    padding: ${({ theme }) => `${theme.spacing(1.5)} 0`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  & dt {
    flex: 0 0 40%;
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    overflow-wrap: anywhere;
  }

  & dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

function unique(values: readonly (string | null)[]): string[] {
  const seen = new Map<string, string>();
  for (const v of values) {
    const t = v?.trim();
    if (t && !seen.has(nameKey(t))) seen.set(nameKey(t), t);
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, 'ru'));
}

interface ModelFormProps {
  /** Модель, которую правят; `null` — новая. */
  model: FleetModel | null;
  formId: string;
  categories: readonly FleetCategory[];
  onCancel: () => void;
  onSaved: (model: FleetModel) => void;
  /** Сколько полей изменено — панель спрашивает перед закрытием. */
  onDirtyChange: (count: number) => void;
}

/**
 * Форма модели: основное и паспорт по блокам схемы. Нетронутые поля уходят
 * ровно такими, какими пришли. На 409 (модель изменили, пока шла правка)
 * форма предлагает перечитать: свежая версия становится основой, правки
 * человека переносятся поверх, поля, которые меняли оба, отмечены.
 */
export function ModelForm({
  model,
  formId,
  categories,
  onCancel,
  onSaved,
  onDirtyChange,
}: ModelFormProps) {
  const [base, setBase] = useState<FleetModel | null>(model);
  const [initial, setInitial] = useState<Record<string, string>>(() => initialFormValues(model));
  const [values, setValues] = useState<Record<string, string>>(() => initialFormValues(model));
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ message: string; fresh: FleetModel } | null>(null);
  const [conflictValues, setConflictValues] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );
  const [carried, setCarried] = useState<number | null>(null);

  const [createModel, { isLoading: creating }] = useCreateFleetModelMutation();
  const [updateModel, { isLoading: updating }] = useUpdateFleetModelMutation();
  const [fetchModel, { isFetching: rereading }] = useLazyGetFleetModelQuery();
  // Подсказки ввода — из всего справочника, а не из отфильтрованного списка.
  const { data: all } = useListFleetModelsQuery({});
  const busy = creating || updating || rereading;

  const dirty = useMemo(() => changedKeys(values, initial), [values, initial]);
  const payload = useMemo(() => buildModelPayload(values, initial, base), [values, initial, base]);
  const issues = useMemo(() => new Map(payload.issues.map((i) => [i.key, i.message])), [payload]);
  const blocks = useMemo(() => buildPassportForm(values), [values]);
  const extras = useMemo(() => unknownPassportEntries(base?.passport ?? {}), [base]);

  useEffect(() => {
    onDirtyChange(dirty.length);
  }, [dirty.length, onDirtyChange]);

  // Ответ сервера с отказом — человек мог быть внизу длинной формы: поднимаем
  // плашку в поле зрения, при конфликте ставим фокус на «Перечитать».
  const noticesRef = useRef<HTMLDivElement>(null);
  const rereadRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!conflict && !serverError) return;
    noticesRef.current?.scrollIntoView({ block: 'start' });
    if (conflict) rereadRef.current?.focus({ preventScroll: true });
  }, [conflict, serverError]);

  // Обновление страницы или закрытие вкладки с несохранённой правкой.
  useEffect(() => {
    if (dirty.length === 0) return;
    const guard = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty.length]);

  const suggestions = useMemo(() => {
    const models = all?.items ?? [];
    return {
      owner: unique(models.map((m) => m.owner_name)),
      position: unique(models.map((m) => m.operator_position)),
      fuel: unique([...FUEL_KINDS, ...models.map((m) => m.fuel_kind)]),
    };
  }, [all]);

  const domId = (key: string) => `${formId}-${key}`;
  const suggestId = (list: SuggestList) => `${formId}-suggest-${list}`;

  const binding: FieldBinding = {
    value: (key) => values[key] ?? '',
    onChange: (key, value) => setValues((prev) => ({ ...prev, [key]: value })),
    onBlur: (key) =>
      setTouched((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      }),
    error: (key) => ((showAll || touched.has(key)) && issues.get(key)) || null,
    conflict: (key) => conflictValues.get(key) ?? null,
    takeConflict: (key) => {
      const fresh = conflictValues.get(key);
      if (fresh === undefined) return;
      setValues((prev) => ({ ...prev, [key]: fresh }));
      setConflictValues((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    },
    domId,
  };

  const errorBlocks = useMemo(() => {
    if (!showAll) return new Set<string>();
    return new Set(
      payload.issues
        .map((i) => formKeyBlock(i.key))
        .filter((b): b is NonNullable<typeof b> => b !== null),
    );
  }, [showAll, payload.issues]);

  // Похожее наименование уже есть — подсказываем до 409 от сервера.
  const nameValue = values[FORM_KEY.name] ?? '';
  const duplicate = useMemo(() => {
    const key = nameKey(nameValue);
    if (!key) return null;
    return (all?.items ?? []).find((m) => m.id !== base?.id && nameKey(m.name) === key) ?? null;
  }, [all, nameValue, base]);

  const categoryValue = values[FORM_KEY.category] ?? '';
  const categoryMatch = useMemo(() => {
    const key = nameKey(categoryValue);
    return key ? (categories.find((c) => nameKey(c.name) === key) ?? null) : null;
  }, [categories, categoryValue]);

  function focusField(key: string) {
    const block = formKeyBlock(key);
    const el = document.getElementById(domId(key));
    if (!el) {
      if (block) jumpToBlock(formId, block);
      return;
    }
    el.scrollIntoView({ block: 'center' });
    el.focus({ preventScroll: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (payload.issues.length > 0) {
      setShowAll(true);
      const first = payload.issues[0];
      if (first) focusField(first.key);
      return;
    }
    if (base && dirty.length === 0) {
      onCancel();
      return;
    }
    try {
      const res = base
        ? await updateModel({
            id: base.id,
            data: payload.data,
            expected_updated_at: base.updated_at,
          }).unwrap()
        : await createModel(payload.data).unwrap();
      onSaved(res.model);
    } catch (err) {
      if (base && apiErrorStatus(err) === 409) {
        // 409 — это и «модель изменили», и «такое наименование уже есть».
        // Различаем по факту: изменилась ли модель на сервере.
        const fresh = await fetchModel(base.id)
          .unwrap()
          .catch(() => null);
        if (fresh && fresh.model.updated_at !== base.updated_at) {
          setConflict({
            message: apiErrorText(err, 'Модель уже изменил кто-то другой.'),
            fresh: fresh.model,
          });
          return;
        }
      }
      setServerError(
        apiErrorText(err, base ? 'Не удалось сохранить модель.' : 'Не удалось добавить модель.'),
      );
    }
  }

  function reread() {
    if (!conflict) return;
    const r = rebaseForm(values, initial, conflict.fresh);
    setBase(conflict.fresh);
    setInitial(r.initial);
    setValues(r.values);
    setConflictValues(new Map(r.conflicts.map((k) => [k, r.initial[k] ?? ''])));
    setCarried(changedKeys(r.values, r.initial).length);
    setConflict(null);
  }

  // В форме статусы без архива — в архив отправляет отдельная кнопка. Если
  // модель всё же в архиве, её статус остаётся в списке, чтобы не подменить.
  const currentStatus = values[FORM_KEY.status] ?? 'active';
  const statusOptions: readonly string[] = FLEET_MODEL_STATUSES.some((s) => s === currentStatus)
    ? FLEET_MODEL_STATUSES
    : [...FLEET_MODEL_STATUSES, currentStatus];

  const issueCount = payload.issues.length;
  const footerText =
    showAll && issueCount > 0
      ? `Проверьте ${issueCount} ${pluralizeRu(issueCount, ['поле', 'поля', 'полей'])}`
      : dirty.length > 0
        ? `Изменено ${dirty.length} ${pluralizeRu(dirty.length, ['поле', 'поля', 'полей'])}`
        : base
          ? 'Изменений нет'
          : 'Заполните наименование — остальное можно добавить позже';

  return (
    <>
      <DrawerBody>
        <Notices ref={noticesRef}>
          {conflict && (
            <Notice $tone="warning" role="alert">
              <span className="text">
                <strong>{conflict.message}</strong> Перечитайте модель: свежая версия станет
                основой, ваши правки перенесутся поверх неё.
              </span>
              <Button
                ref={rereadRef}
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={reread}
              >
                Перечитать
              </Button>
            </Notice>
          )}
          {carried !== null && (
            <Notice $tone="info" role="status">
              <span className="text">
                {typo(
                  `Загружена свежая версия. Перенесено ваших правок: ${carried}. ${
                    conflictValues.size > 0
                      ? `В ${conflictValues.size} ${pluralizeRu(conflictValues.size, ['поле', 'полях', 'полях'])} коллега тоже менял значение — они отмечены ниже.`
                      : 'Проверьте и сохраните ещё раз.'
                  }`,
                )}
              </span>
            </Notice>
          )}
          {serverError && (
            <Notice $tone="danger" role="alert">
              <span className="text">{serverError}</span>
            </Notice>
          )}
        </Notices>

        <form id={formId} onSubmit={submit} noValidate>
          <BlockSection aria-labelledby={`${formId}-main-h`}>
            <BlockHeading id={`${formId}-main-h`}>Основное</BlockHeading>
            <FormGrid>
              <FieldShell
                binding={binding}
                k={FORM_KEY.name}
                label="Марка, модель, категория ТС"
                required
                wide
                hint={typo(
                  'Ключ модели, как в книге ЭММ: по нему модель сопоставляется при загрузке книги.',
                )}
                warning={
                  duplicate
                    ? typo(
                        `Модель «${duplicate.name}» уже есть в справочнике — две модели с одним наименованием завести нельзя.`,
                      )
                    : undefined
                }
              >
                <Control
                  id={domId(FORM_KEY.name)}
                  value={values[FORM_KEY.name] ?? ''}
                  aria-required="true"
                  aria-invalid={binding.error(FORM_KEY.name) || duplicate ? true : undefined}
                  aria-describedby={[
                    `${domId(FORM_KEY.name)}-hint`,
                    duplicate ? `${domId(FORM_KEY.name)}-warning` : null,
                    binding.error(FORM_KEY.name) ? `${domId(FORM_KEY.name)}-error` : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  autoComplete="off"
                  onChange={(e) => binding.onChange(FORM_KEY.name, e.target.value)}
                  onBlur={() => binding.onBlur(FORM_KEY.name)}
                />
              </FieldShell>
              <FieldShell
                binding={binding}
                k={FORM_KEY.category}
                label="Категория техники"
                hint={
                  categoryValue.trim() === ''
                    ? 'Группа, как в книге ЭММ: «Бульдозеры», «Экскаваторы»…'
                    : categoryMatch
                      ? categoryMatch.name === categoryValue.trim()
                        ? 'Категория из справочника'
                        : `Будет выбрана категория «${categoryMatch.name}»`
                      : 'Новая категория — появится в справочнике после сохранения'
                }
              >
                <Control
                  id={domId(FORM_KEY.category)}
                  value={categoryValue}
                  list={`${formId}-categories`}
                  aria-describedby={`${domId(FORM_KEY.category)}-hint`}
                  autoComplete="off"
                  onChange={(e) => binding.onChange(FORM_KEY.category, e.target.value)}
                  onBlur={() => binding.onBlur(FORM_KEY.category)}
                />
              </FieldShell>
              <FieldShell binding={binding} k={FORM_KEY.status} label="Статус">
                <Select
                  id={domId(FORM_KEY.status)}
                  value={values[FORM_KEY.status] ?? 'active'}
                  onChange={(e) => binding.onChange(FORM_KEY.status, e.target.value)}
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>
                      {isFleetStatus(s) ? FLEET_STATUS_LABEL[s] : s}
                    </option>
                  ))}
                </Select>
              </FieldShell>
              <FieldShell binding={binding} k={FORM_KEY.comment} label="Комментарий" wide>
                <ControlArea
                  id={domId(FORM_KEY.comment)}
                  value={values[FORM_KEY.comment] ?? ''}
                  rows={2}
                  onChange={(e) => binding.onChange(FORM_KEY.comment, e.target.value)}
                />
              </FieldShell>
            </FormGrid>
            <Legend>
              {typo(
                'Поля с меткой ПРЦ участвуют в расчёте цены машино-часа: их правка пометит ПРЦ модели устаревшей. Числа — с запятой или точкой, разряды можно разделять пробелом.',
              )}
            </Legend>
          </BlockSection>

          <PassportBlockNav blocks={blocks} idPrefix={formId} alerts={errorBlocks} />

          {blocks.map((b, i) => (
            <BlockSection
              key={b.id}
              id={blockAnchor(formId, b.id)}
              aria-labelledby={`${blockAnchor(formId, b.id)}-h`}
            >
              <BlockHeading id={`${blockAnchor(formId, b.id)}-h`} tabIndex={-1}>
                <span className="n" aria-hidden="true">
                  {blockNumber(i)}
                </span>
                {b.title}
              </BlockHeading>
              <FormGrid>
                {b.items.map((item) => {
                  if (item.kind === 'field') {
                    if (item.key === FORM_KEY.units) {
                      return (
                        <FieldShell
                          key={item.key}
                          binding={binding}
                          k={item.key}
                          label={item.label}
                        >
                          <Control
                            id={domId(item.key)}
                            value={values[item.key] ?? ''}
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            aria-invalid={binding.error(item.key) ? true : undefined}
                            aria-describedby={
                              binding.error(item.key) ? `${domId(item.key)}-error` : undefined
                            }
                            onChange={(e) => binding.onChange(item.key, e.target.value)}
                            onBlur={() => binding.onBlur(item.key)}
                          />
                        </FieldShell>
                      );
                    }
                    return (
                      <ScalarInput
                        key={item.key}
                        item={item}
                        binding={binding}
                        suggestId={suggestId}
                      />
                    );
                  }
                  if (item.kind === 'matrix')
                    return <MatrixInput key={item.id} item={item} binding={binding} />;
                  return <PairsInput key={item.code} item={item} binding={binding} />;
                })}
              </FormGrid>
            </BlockSection>
          ))}

          {extras.length > 0 && (
            <BlockSection aria-labelledby={`${formId}-other-h`}>
              <BlockHeading id={`${formId}-other-h`}>Прочее</BlockHeading>
              <BlockNote>
                {typo(
                  'Поля, которых нет в схеме паспорта. Здесь они не правятся и сохраняются без изменений.',
                )}
              </BlockNote>
              <Extras>
                {extras.map(([code, text]) => (
                  <div key={code}>
                    <dt>{code}</dt>
                    <dd>{text}</dd>
                  </div>
                ))}
              </Extras>
            </BlockSection>
          )}

          <datalist id={`${formId}-categories`}>
            {categories.map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
          {SUGGEST_LISTS.map((list) => (
            <datalist key={list} id={suggestId(list)}>
              {suggestions[list].map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          ))}
        </form>
      </DrawerBody>
      <DrawerFooter>
        <FooterStatus role="status">
          {conflict ? 'Модель изменили, пока шла правка' : footerText}
          {conflict && (
            <>
              {' · '}
              <TextButton type="button" onClick={reread}>
                перечитать
              </TextButton>
            </>
          )}
          {!conflict && showAll && issueCount > 0 && (
            <>
              {' · '}
              <TextButton
                type="button"
                onClick={() => {
                  const first = payload.issues[0];
                  if (first) focusField(first.key);
                }}
                title={payload.issues.map((i) => formKeyLabel(i.key)).join('; ')}
              >
                к первому
              </TextButton>
            </>
          )}
        </FooterStatus>
        <Actions>
          <Button type="button" $variant="ghost" onClick={onCancel} disabled={busy}>
            Отмена
          </Button>
          <Button
            type="submit"
            form={formId}
            $variant="primary"
            disabled={busy || conflict !== null}
          >
            {creating || updating ? 'Сохраняю…' : base ? 'Сохранить' : 'Добавить модель'}
          </Button>
        </Actions>
      </DrawerFooter>
    </>
  );
}
